import useDebounce from 'shared/composables/debounce';
import { onMounted, onUnmounted, ref } from 'vue';

export default function useDragResize({ min, max, reversed, defaultValue = 0, storageKey, onEnd } = {}) {
  const { debounce } = useDebounce();
  const dragging = ref(false);
  let startPos = 0;
  let startValue = 0;
  let currentAxis = 'x';
  const direction = reversed ? -1 : 1;

  // `min` and `max` may be a number OR a function returning a number so a caller
  // can cap to a viewport-dependent value (e.g. 50vw).
  const resolveBound = (bound) => (typeof bound === 'function' ? bound() : bound);

  const clamp = (v) => {
    // Guard against NaN / non-finite leaking through from a corrupted stored
    // value or a bad delta; fall back to defaultValue rather than propagating.
    if (!Number.isFinite(v)) {
      v = Number.isFinite(defaultValue) ? defaultValue : 0;
    }
    const lo = resolveBound(min);
    const hi = resolveBound(max);
    if (Number.isFinite(lo)) v = Math.max(lo, v);
    if (Number.isFinite(hi)) v = Math.min(hi, v);
    return v;
  };

  let initialValue = defaultValue;
  if (storageKey) {
    const stored = localStorage.getItem(storageKey);
    const parsed = stored !== null ? parseInt(stored, 10) : NaN;
    if (Number.isFinite(parsed)) {
      initialValue = clamp(parsed);
    } else if (stored !== null) {
      // Storage had a non-numeric / NaN value — purge so we don't keep
      // round-tripping garbage.
      localStorage.removeItem(storageKey);
    }
  }
  const value = ref(initialValue);

  const start = (event, axis) => {
    event.stopPropagation();
    event.preventDefault();
    document.body.classList.add('no-interaction');
    dragging.value = true;
    currentAxis = axis;
    startPos = axis === 'x' ? event.clientX : event.clientY;
    startValue = value.value;
    window.addEventListener('mousemove', onDrag);
    window.addEventListener('mouseup', end);
  };

  const handleDragStartX = (event) => start(event, 'x');
  const handleDragStartY = (event) => start(event, 'y');

  const onDrag = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (!dragging.value) {
      return;
    }
    const currentPos = currentAxis === 'x' ? event.clientX : event.clientY;
    const delta = (currentPos - startPos) * direction;
    debounce(() => { value.value = clamp(startValue + delta); }, 5, 'drag-resize:drag');
  };

  const end = () => {
    window.removeEventListener('mousemove', onDrag);
    window.removeEventListener('mouseup', end);
    document.body.classList.remove('no-interaction');
    dragging.value = false;
    if (storageKey && Number.isFinite(value.value)) {
      localStorage.setItem(storageKey, value.value);
    }
    onEnd?.(value.value);
  };

  // If `max`/`min` are viewport-dependent, shrinking the window leaves a
  // previously-dragged or stored value above the new cap. Re-clamp on resize
  // so the value stays within bounds without any caller action. Uses an
  // explicit debounce key to avoid colliding with onDrag's debounce entry.
  const reclampOnResize = () => {
    debounce(() => { value.value = clamp(value.value); }, 50, 'drag-resize:reclamp');
  };

  onMounted(() => {
    window.addEventListener('resize', reclampOnResize);
  });

  onUnmounted(() => {
    window.removeEventListener('mousemove', onDrag);
    window.removeEventListener('mouseup', end);
    window.removeEventListener('resize', reclampOnResize);
  });

  return {
    value,
    dragging,
    handleDragStartX,
    handleDragStartY,
  };
}
